/**
 * 点位图标
 * - jcss：基础设施设备图标（蓝正常 / 红告警 / 灰离线）
 * - pointIcon：业务图例图标（WAGA/SQ 等，作兜底）
 */
import * as Cesium from "cesium";

function loadIconContext(ctx) {
  const map = {};
  ctx.keys().forEach((key) => {
    const file = key.replace(/^\.\//, "");
    const name = file.replace(/\.(svg|png)$/i, "");
    map[name] = ctx(key);
  });
  return map;
}

const jcssContext = require.context("@/assets/jcss", false, /\.(svg|png)$/i);
const pointIconContext = require.context(
  "@/assets/pointIcon",
  false,
  /\.(svg|png)$/i
);

const JCSS_ICONS = loadIconContext(jcssContext);
const POINT_ICONS = loadIconContext(pointIconContext);

/** 图层 key → jcss 设备名 */
export const LAYER_ICON_NAME = {
  gate: "闸门",
  warehouse: "控制柜",
  shelter: "控制柜",
  rescue: "控制柜",
  sq: "液位计",
  yq: "流量计",
  jsjc: "液位计",
  gq: "闸门",
  drainPump: "排水泵",
  agriPump: "潜污泵",
  wwtp: "鼓风机",
  drainUser: "垃圾收集器",
  supplyPump: "取水泵",
  supplyPressure: "流量计",
  supplyHydrant: "穿墙泵",
  drainManhole: "液位计",
};

/** mapLegendSign / dataType → jcss 名（或 pointIcon 英文名） */
const SIGN_ICON_NAME = {
  WAGA: "闸门",
  FZCB: "控制柜",
  FXESCAPINGCENTER: "控制柜",
  FXRESCUETEAM: "控制柜",
  SQ: "液位计",
  YQ: "流量计",
  YJSD: "液位计",
  GQ: "闸门",
  GSGW: "流量计",
  EGBF: "取水泵",
  PSH: "垃圾收集器",
  PSBZ: "排水泵",
  WSCLC: "鼓风机",
  PUST: "穿墙泵",
  PSJG: "液位计",
  JSJG: "液位计",
  PSGW: "排水泵",
  YSGW: "潜污泵",
  WQJC: "流量计",
  HHJC: "液位计",
  GCJC: "控制柜",
  ZNKZ: "控制柜",
  C_ZDDX: "控制柜",
  C_ZDDX1: "控制柜",
};

/**
 * jcss 状态后缀：
 *  无后缀 = 正常（蓝）
 *  1 = 告警（红）
 *  2 = 离线/停用（灰）
 */
function jcssUrl(baseName, { warn = false, offline = false } = {}) {
  if (!baseName) return null;
  let suffix = "";
  if (warn) suffix = "1";
  else if (offline) suffix = "2";

  const ordered = suffix
    ? [`${baseName}${suffix}`, baseName, `${baseName}1`, `${baseName}2`]
    : [baseName, `${baseName}1`, `${baseName}2`];

  for (const key of ordered) {
    if (JCSS_ICONS[key]) return JCSS_ICONS[key];
  }
  return null;
}

function pointIconUrl(name) {
  if (!name) return null;
  return (
    POINT_ICONS[name] ||
    POINT_ICONS[String(name).toUpperCase()] ||
    null
  );
}

function isOffline(item = {}) {
  const s = String(item.networkingStatus ?? item.status ?? "");
  if (!s) return false;
  return /离线|断开|掉线|offline/i.test(s) || s === "0";
}

/**
 * 解析点位图标 URL（优先 jcss，再 pointIcon）
 * @param {object} cfg
 * @param {object} [item]
 * @param {{ warn?: boolean, offline?: boolean }} [status]
 */
export function resolvePointIconUrl(cfg, item = {}, status = {}) {
  const warn =
    status.warn ??
    (item.isWarn === true || item.isWarn === 1 || item.isWarn === "1");
  const offline = status.offline ?? isOffline(item);

  const candidates = [
    cfg?.icon,
    item.mapLegendSign,
    item.dataType,
    LAYER_ICON_NAME[cfg?.key],
  ];

  const mt = item.monitorType ? String(item.monitorType) : "";
  if (mt) {
    if (mt.startsWith("psbz")) candidates.push("排水泵");
    if (mt.startsWith("SC") || mt.startsWith("sc")) candidates.push("鼓风机");
    if (mt === "psh") candidates.push("垃圾收集器");
    if (mt === "egbf") candidates.push("取水泵");
    if (mt === "gsgw") candidates.push("流量计");
    if (mt === "hdsw") candidates.push("液位计");
    if (mt === "yl") candidates.push("流量计");
    if (mt === "yjsdsw") candidates.push("液位计");
    if (mt === "zmkz" || mt === "bzkz") candidates.push("闸门");
    if (mt === "psbznw") candidates.push("潜污泵");
  }

  for (const c of candidates) {
    if (!c) continue;
    const mapped =
      SIGN_ICON_NAME[c] || SIGN_ICON_NAME[String(c).toUpperCase()] || c;

    // 1) jcss 中文设备名
    const jcss = jcssUrl(mapped, { warn, offline });
    if (jcss) return jcss;

    // 2) pointIcon 英文图例
    const pi = pointIconUrl(mapped) || pointIconUrl(c);
    if (pi) return pi;
  }

  return jcssUrl("控制柜", { warn, offline }) || pointIconUrl("C_ZDDX");
}

export function makeBillboardOptions(
  imageUrl,
  { warn = false, size = 42 } = {}
) {
  if (!imageUrl) return undefined;
  return {
    image: imageUrl,
    width: size,
    height: size,
    scale: 1,
    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
    heightReference: Cesium.HeightReference.NONE,
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
    // 远景略缩小，近景不再放大（避免看起来比设定值更大）
    scaleByDistance: new Cesium.NearFarScalar(2000, 1.0, 45000, 0.55),
    color: Cesium.Color.WHITE,
  };
}

export function makePointLabelOptions(
  name,
  { offsetY = -34, far = 12000 } = {}
) {
  return {
    text: String(name),
    // 系统字体在 Cesium 画布上更清晰；避免 scale 后发糊
    font: "14px PingFang SC, Microsoft YaHei, sans-serif",
    fillColor: Cesium.Color.fromCssColorString("#ffffff"),
    outlineColor: Cesium.Color.fromCssColorString("#031018"),
    outlineWidth: 2,
    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
    showBackground: true,
    backgroundColor: Cesium.Color.fromCssColorString("rgba(2, 10, 18, 0.78)"),
    backgroundPadding: new Cesium.Cartesian2(5, 3),
    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
    pixelOffset: new Cesium.Cartesian2(0, offsetY),
    // 偏移不随距离缩放，避免字与图标间距被拉大
    pixelOffsetScaleByDistance: new Cesium.NearFarScalar(1.0e3, 1.0, 5.0e4, 1.0),
    heightReference: Cesium.HeightReference.NONE,
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
    scale: 1.0,
    // 近中景保持 1.0，远景轻微缩小（过小缩放会糊）
    scaleByDistance: new Cesium.NearFarScalar(2500, 1.0, 20000, 0.85),
    // 默认视角约 18km，远距隐藏文字，只保留图标/聚合，避免叠字
    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, far),
  };
}
